import React, { useState } from 'react';
import NumberBadge from './NumberBadge';
import {
  Search,
  Download,
  CheckCheck,
  Clock,
  XCircle,
  Send,
  RefreshCw,
  Eye
} from 'lucide-react';
import moment from 'moment';
import { useListStatusesQuery, SendLogItem } from '@/client/api/whatsapp/list-statuses.query';

type DeliveryStatus = 'delivered' | 'read' | 'sent' | 'failed' | 'pending';

const statusConfig: Record<DeliveryStatus, { label: string; style: string; icon: React.ReactNode }> = {
  read: { label: 'Read', style: 'bg-blue-100 text-blue-700 border-blue-200', icon: <CheckCheck className="w-3 h-3" /> },
  delivered: { label: 'Delivered', style: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCheck className="w-3 h-3" /> },
  sent: { label: 'Sent', style: 'bg-gray-100 text-gray-700 border-gray-200', icon: <Send className="w-3 h-3" /> },
  failed: { label: 'Failed', style: 'bg-red-100 text-red-700 border-red-200', icon: <XCircle className="w-3 h-3" /> },
  pending: { label: 'Pending', style: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: <Clock className="w-3 h-3" /> }
};

const statusFor = (log: SendLogItem): DeliveryStatus => log.delivery_status || 'pending';

const previewFor = (log: SendLogItem) =>
  log.payload?.text?.body || log.payload?.body ||
  (log.payload?.template_name ? `Template: ${log.payload.template_name}` : '[message]');

const DeliveryStatusLogPage: React.FC = () => {
  const { data, isLoading, refetch } = useListStatusesQuery();
  const logs = data?.data?.statuses || [];
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = logs.filter(l => {
    const matchesSearch = l.to.includes(searchQuery) || (l.wamid || '').includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || statusFor(l) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleExport = () => {
    const rows = [['Message ID', 'To', 'Status', 'Sent At'], ...logs.map(l => [
      l.wamid || '',
      l.to,
      statusFor(l),
      moment.unix(l.timestamp).format('YYYY-MM-DD HH:mm:ss'),
    ])];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'whatsapp-delivery-log.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Delivery Status Log</h1>
            <p className="text-sm text-gray-500 mt-1">Track the delivery status of outgoing WhatsApp messages</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {(Object.keys(statusConfig) as DeliveryStatus[]).map(status => {
            const cfg = statusConfig[status];
            const count = logs.filter(l => statusFor(l) === status).length;
            return (
              <div key={status} className="bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${cfg.style}`}>
                  {cfg.icon}{cfg.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by phone or message ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="read">Read</option>
                <option value="delivered">Delivered</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr><td colSpan={6} className="px-6 py-4 text-sm text-gray-500 text-center">Loading...</td></tr>
                )}
                {filtered.map(log => {
                  const status = statusFor(log);
                  const cfg = statusConfig[status];
                  const rowId = log.wamid || String(log.timestamp);
                  return (
                    <React.Fragment key={rowId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-600">{log.wamid || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div>{log.to}</div>
                          <div className="mt-1"><NumberBadge id={log.from_number_id} /></div>
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <p className="text-sm text-gray-600 truncate">{previewFor(log)}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.style}`}>
                            {cfg.icon}{cfg.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment.unix(log.timestamp).format('YYYY-MM-DD HH:mm:ss')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => setExpandedId(expandedId === rowId ? null : rowId)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                        </td>
                      </tr>
                      {expandedId === rowId && (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 bg-gray-50">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Action</p>
                                <p className="text-gray-900">{log.action || 'send'}</p>
                              </div>
                              {log.status_updated_at && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Last Status Update</p>
                                  <p className="text-gray-900">{moment.unix(log.status_updated_at).format('YYYY-MM-DD HH:mm:ss')}</p>
                                </div>
                              )}
                              {log.errors && log.errors.length > 0 && (
                                <div className="col-span-2">
                                  <p className="text-xs font-medium text-red-500 uppercase mb-1">Errors</p>
                                  {log.errors.map((err, i) => (
                                    <p key={i} className="text-red-600 font-mono text-xs">{err.code}: {err.title || err.message}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12">
              <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No delivery logs found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryStatusLogPage;
